import { Button, Divider, Stack, Typography } from "@mui/material";
import { panelStyle } from "@/theme";
import { WellnessScoreStatus } from "../types";
import { formatDateToISO } from "../helpers";

export interface MetricItem {
  label: string;
  value: string;
  unit: string;
  status?: WellnessScoreStatus;
  statusLabel?: string;
  children?: React.ReactNode;
}

export interface MetricValueCardDoubleProps {
  metricName: string;
  lastUpdated?: string;
  metrics: MetricItem[];
  ctaText: string;
  onCtaClick?: () => void;
}

export default function MetricValueCardDouble(
  props: MetricValueCardDoubleProps
): JSX.Element {
  const { metricName, lastUpdated, metrics, ctaText, onCtaClick } = props;

  const getStatusColor = (status: WellnessScoreStatus | undefined) => {
    switch (status) {
      case WellnessScoreStatus.Healthy:
        return "accent1.main";
      case WellnessScoreStatus.NeedsAttention:
        return "secondary.700";
      case WellnessScoreStatus.NeedsSupport:
        return "error.main";
      default:
        return "neutral.500";
    }
  };

  const getStatusTextColor = (status: WellnessScoreStatus | undefined) => {
    switch (status) {
      case WellnessScoreStatus.NeedsAttention:
        return "neutral.900";
      default:
        return "primary.contrastText";
    }
  };

  return (
    <Stack sx={{ ...panelStyle }} width="100%">
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          fontWeight={600}
          sx={{
            fontFamily: "Manrope",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: 1.25,
            letterSpacing: "-0.03em",
          }}
        >
          {metricName}
        </Typography>
        {lastUpdated && (
          <Typography
            variant="body2"
            sx={{
              color: "neutral.500",
              fontSize: 14,
            }}
          >
            {formatDateToISO(new Date(lastUpdated))}
          </Typography>
        )}
      </Stack>

      {metrics.map((metric, index) => (
        <Stack key={index}>
          {index > 0 && <Divider sx={{ my: 2 }} />}

          <Stack mt={index === 0 ? 2 : 0}>
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: 14,
                color: "neutral.900",
              }}
            >
              {metric.label}
            </Typography>

            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              mt={0.5}
            >
              <Stack flexDirection="row" alignItems="baseline" gap={0.5}>
                <Typography
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 700,
                    fontSize: 28,
                    lineHeight: 1.2,
                    color: getStatusColor(metric.status),
                  }}
                >
                  {metric.value}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: "neutral.700",
                  }}
                >
                  {metric.unit}
                </Typography>
              </Stack>

              {metric.statusLabel && (
                <Stack
                  sx={{
                    backgroundColor: getStatusColor(metric.status),
                    borderRadius: 1,
                    alignSelf: "start",
                  }}
                  padding={1}
                >
                  <Typography
                    fontWeight={600}
                    fontSize={12}
                    variant="body2"
                    color={getStatusTextColor(metric.status)}
                  >
                    {metric.statusLabel}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {metric.children && (
              <Stack mt={2} width="100%">
                {metric.children}
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}

      <Stack mt={2} width="100%">
        <Button
          variant="outlined"
          color="primary"
          onClick={onCtaClick}
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {ctaText}
        </Button>
      </Stack>
    </Stack>
  );
}
